import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAxios } from "../hooks/useAxios";
import { useUpload } from "../hooks/useUpload";
import "../styles/RequestForm.css";
import type {
  SubmissionStatus,
  ReapplyStatus,
  ArtType,
} from "@noms_portfolio/common";

interface Registration {
  email: string;
  hash: string;
  vip: boolean;
  fastlane: boolean;
  status: string;
}

interface Submission {
  id: string;
  name?: string;
  email?: string;
  artType?: ArtType;
  deadline?: any;
  publicPermission?: boolean;
  contact?: {
    email?: string;
    discord?: string;
    x?: string;
  };
  detail?: string;
  referenceLinks?: string[];
  attachments?: Array<{ url: string; previewUrl: string }>;
  status: SubmissionStatus;
  reapplyStatus?: ReapplyStatus;
  createdAt: any;
  updatedAt?: any;
}

interface RequestData {
  registration: Registration;
  submissions: Submission[];
}

export const RequestForm: React.FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const navigate = useNavigate();
  const { axiosClient } = useAxios();
  const { uploadImages, uploading, progress, error: uploadError } = useUpload();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RequestData | null>(null);
  const [error, setError] = useState("");
  const [reapplyError, setReapplyError] = useState("");
  const [commissionClosed, setCommissionClosed] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    artType: "" as "standing" | "single" | "single-layered" | "",
    deadline: "",
    publicPermission: false,
    contactType: "email" as "email" | "discord" | "x",
    contactValue: "",
    detail: "",
    links: [""],
    attachments: [] as Array<{
      url: string;
      previewUrl: string;
    }>,
  });

  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isCancelled, setIsCancelled] = useState(false);

  // 編集中のsubmission ID (既存の場合)
  const [editingSubmissionId, setEditingSubmissionId] = useState<string | null>(
    null
  );

  const [isEditable, setIsEditable] = useState(true);

  const currentSubmission =
    editingSubmissionId && editingSubmissionId !== "new"
      ? data?.submissions.find((s) => s.id === editingSubmissionId) || null
      : null;

  // Cancel request handler
  const handleCancelRequest = async () => {
    if (!editingSubmissionId) return;
    try {
      await axiosClient.post(`/api/requests/${editingSubmissionId}/cancel`);
      setSuccessMessage("Your request has been cancelled.");
      setIsEditable(false);
      setIsCancelled(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to cancel request."
      );
    }
  };

  const statusLabel = (status?: SubmissionStatus) => {
    if (!status) return "Unknown";
    switch (status) {
      case "pending":
        return "Reviewing Request";
      case "approved":
        return "Work In Progress";
      case "rejected":
        return "Temporarily Suspended Due To Reaching Limit";
      case "completed":
        return "Completed";
      case "cancelled":
        return "Cancelled By User";
      case "auto_cancelled":
        return "As there was no reply, it has been cancelled.";
      default:
        return status;
    }
  };

  // pendingとrejectedは編集可能
  const isEditableStatus = (status?: string) =>
    status === "pending" || status === "rejected";

  useEffect(() => {
    if (!hash) {
      setError("Invalid URL");
      setLoading(false);
      return;
    }

    loadRequestData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  // 編集対象が選択されたらフォームに反映（VIPユーザーの一覧→編集遷移対応）
  useEffect(() => {
    if (!data) return;
    // 新規作成の場合は初期化
    if (editingSubmissionId === "new") {
      setIsEditable(true);
      setFormData({
        name: "",
        artType: "",
        deadline: "",
        publicPermission: false,
        contactType: "email",
        contactValue: data.registration.email,
        detail: "",
        links: [""],
        attachments: [],
      });
      return;
    }

    if (editingSubmissionId) {
      const existing = data.submissions.find(
        (s) => s.id === editingSubmissionId
      );
      if (existing) {
        setIsEditable(isEditableStatus(existing.status));
        setFormData({
          name: existing.name || "",
          artType: (existing.artType as any) || "",
          deadline: existing.deadline
            ? new Date(existing.deadline._seconds * 1000)
                .toISOString()
                .split("T")[0]
            : "",
          publicPermission: existing.publicPermission || false,
          contactType: existing.contact?.email
            ? "email"
            : existing.contact?.discord
              ? "discord"
              : "x",
          contactValue:
            existing.contact?.email ||
            existing.contact?.discord ||
            existing.contact?.x ||
            "",
          detail: existing.detail || "",
          links:
            Array.isArray(existing.referenceLinks) &&
            existing.referenceLinks.length > 0
              ? existing.referenceLinks
              : [""],
          attachments: Array.isArray(existing.attachments)
            ? existing.attachments.map((a) => ({
                url: a.url,
                previewUrl: a.previewUrl || "",
              }))
            : [],
        });
      }
    }
  }, [editingSubmissionId, data]);

  const loadRequestData = async () => {
    try {
      setLoading(true);
      setError("");

      // コミッション受付状態を確認
      const statusResponse = await axiosClient.get("/api/waiting/status");
      const isOpen = statusResponse.data.isOpen;
      setCommissionClosed(!isOpen);

      const response = await axiosClient.get(`/api/requests/${hash}`);

      if (response.data.ok) {
        const requestData: RequestData = {
          ...response.data,
          submissions: response.data.submissions || [],
        };
        setData(requestData);

        // 依頼がない場合は新規フォームをデフォルトで表示
        const showNewForm = response.data.showNewForm === true;
        if (showNewForm && (isOpen || requestData.registration.vip)) {
          // 新規フォームの場合、contactValueにemailをセット（VIPは受付停止中でも作成可能）
          setFormData((prev) => ({
            ...prev,
            contactValue: requestData.registration.email,
          }));
          setEditingSubmissionId("new");
          return;
        }

        // VIPユーザー以外で既に提出済みの場合、フォームに既存データを設定
        if (
          !requestData.registration.vip &&
          requestData.submissions?.length > 0
        ) {
          const existing = requestData.submissions[0];
          setEditingSubmissionId(existing.id);
          setIsEditable(isEditableStatus(existing.status));

          // フォームに既存データを埋める
          setFormData({
            name: existing.name || "",
            artType: existing.artType || "",
            deadline: existing.deadline
              ? new Date(existing.deadline._seconds * 1000)
                  .toISOString()
                  .split("T")[0]
              : "",
            publicPermission: existing.publicPermission || false,
            contactType: existing.contact?.email
              ? "email"
              : existing.contact?.discord
                ? "discord"
                : "x",
            contactValue:
              existing.contact?.email ||
              existing.contact?.discord ||
              existing.contact?.x ||
              "",
            detail: existing.detail || "",
            links:
              Array.isArray(existing.referenceLinks) &&
              existing.referenceLinks.length > 0
                ? existing.referenceLinks
                : [""],
            attachments: Array.isArray(existing.attachments)
              ? existing.attachments.map((a) => ({
                  url: a.url,
                  previewUrl: a.previewUrl,
                }))
              : [],
          });
        } else if (!requestData.registration.vip) {
          // 非VIPで新規作成の場合はemailだけセット（受付開始時のみ）
          setFormData((prev) => ({
            ...prev,
            contactValue: requestData.registration.email,
          }));
          if (isOpen) {
            setEditingSubmissionId("new");
          }
        }
      }
    } catch (err: any) {
      console.error("Failed to load request data", err);

      if (err.response?.status === 404) {
        setError(
          "This URL does not exist. Please check the correct URL from your registered email address."
        );
      } else if (err.response?.status === 403) {
        setCommissionClosed(true);
      } else {
        setError(
          err.response?.data?.message ||
            "Failed to load data. Please check if the URL is correct."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEditable) {
      setError("This request has already been approved and cannot be edited.");
      return;
    }

    if (
      commissionClosed &&
      (!editingSubmissionId || editingSubmissionId === "new") &&
      !data?.registration.vip
    ) {
      setError(
        "New requests cannot be created as submissions are currently closed."
      );
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccessMessage("");

    try {
      const contact: any = {};
      if (formData.contactType === "email")
        contact.email = formData.contactValue;
      if (formData.contactType === "discord")
        contact.discord = formData.contactValue;
      if (formData.contactType === "x") contact.x = formData.contactValue;

      // 参考リンクから空文字を除外
      const referenceLinks = formData.links.filter((l) => l.trim() !== "");

      // バックエンドは ISO8601 の datetime を期待
      const deadline = formData.deadline
        ? new Date(formData.deadline).toISOString()
        : null;

      // 添付ファイルの形式を変換（attachmentSchemaに準拠）
      const attachments = formData.attachments.map((att) => ({
        url: att.url,
        previewUrl: att.previewUrl,
      }));

      const payload = {
        userHash: hash!,
        name: formData.name || undefined,
        artType: formData.artType || undefined,
        deadline,
        publicPermission: formData.publicPermission,
        contact,
        detail: formData.detail || undefined,
        referenceLinks: referenceLinks.length ? referenceLinks : undefined,
        attachments: attachments.length ? attachments : undefined,
      };

      if (editingSubmissionId && editingSubmissionId !== "new") {
        // 更新（PATCH /api/requests/:id）
        await axiosClient.patch(
          `/api/requests/${editingSubmissionId}`,
          payload
        );
        setSuccessMessage("Your request has been updated.");
      } else {
        // 新規作成（POST /api/requests/submit）
        const response = await axiosClient.post(
          "/api/requests/submit",
          payload
        );
        if (response.data.success) {
          setSuccessMessage("Your request has been submitted!");
          setEditingSubmissionId(response.data.request.id);
          setIsEditable(true); // 新規作成直後はまだ編集可能
        }
      }
    } catch (err: any) {
      console.error("Failed to submit request", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to submit request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddLink = () => {
    setFormData((prev) => ({ ...prev, links: [...prev.links, ""] }));
  };

  const handleRemoveLink = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }));
  };

  const handleLinkChange = (index: number, value: string) => {
    const newLinks = [...formData.links];
    newLinks[index] = value;
    setFormData((prev) => ({ ...prev, links: newLinks }));
  };

  // VIPユーザーの一覧に戻る際に最新の依頼一覧を取得
  const handleBackToList = async () => {
    await loadRequestData();
    setEditingSubmissionId(null);
  };

  const renderStatusNotice = () => {
    if (!currentSubmission) return null;

    if (currentSubmission.status === "completed") {
      return (
        <div className="info-message">
          <p>
            This commission has been completed. If you would like to request
            another illustration, please register your email address again from
            the home page.
          </p>
          <button onClick={() => navigate("/")} className="btn-back">
            Back to Home
          </button>
        </div>
      );
    }

    if (currentSubmission.status === "cancelled") {
      return (
        <div className="info-message cancelled-message">
          <p>
            This request has been cancelled and will not be processed. If you
            wish to submit a new commission, please re-register your email
            address from the home page.
          </p>
          <button onClick={() => navigate("/")} className="btn-back">
            Back to Home
          </button>
        </div>
      );
    }

    if (currentSubmission.status === "auto_cancelled") {
      return (
        <div className="info-message cancelled-message">
          <p>
            This request has been cancelled as there was no response for some
            time. Should you wish to resubmit your request, please send it via
            this form.
          </p>
          <button
            onClick={() => {
              // 新規申請フローへ遷移（新規フォーム表示）
              setEditingSubmissionId("new");
              setIsCancelled(false);
              setSuccessMessage("");
            }}
            className="btn-back"
          >
            Apply Again
          </button>
        </div>
      );
    }

    // 再開通知を受け取ったユーザー向けの再依頼ボタン表示
    if (
      currentSubmission.status === "rejected" &&
      currentSubmission.reapplyStatus === "waiting"
    ) {
      return (
        <div className="reapply-notice">
          <p>
            <strong>Commission Slots Are Now Available!</strong>
          </p>
          <p>
            Thank you for your patience. We are ready to proceed with your
            request. Please choose one of the following options:
          </p>
          {/* 再案内時の返信ボタン群 */}
          <div className="reapply-actions" style={{ marginTop: 16 }}>
            <button
              className="btn-submit"
              onClick={async () => {
                setReapplyError("");
                try {
                  // 内容を変更してリクエストを続ける
                  await axiosClient.post(
                    `/api/requests/${currentSubmission.id}/reapply`,
                    { action: "edit" }
                  );
                  setIsEditable(true);
                  setSuccessMessage("");
                  await loadRequestData();
                } catch (err: any) {
                  setReapplyError(
                    err.response?.data?.message || "Failed to process request."
                  );
                }
              }}
              style={{ marginRight: 8 }}
            >
              Continue with Changes
            </button>
            <button
              className="btn-submit"
              onClick={async () => {
                setReapplyError("");
                try {
                  // 内容を変更せずにリクエストを続ける
                  await axiosClient.post(
                    `/api/requests/${currentSubmission.id}/reapply`,
                    { action: "noedit" }
                  );
                  setIsEditable(false);
                  setSuccessMessage(
                    "Thank you! Your request has been confirmed without changes."
                  );
                  await loadRequestData();
                } catch (err: any) {
                  setReapplyError(
                    err.response?.data?.message || "Failed to process request."
                  );
                }
              }}
              style={{ marginRight: 8 }}
            >
              Continue without Changes
            </button>
            <button
              className="btn-cancel"
              onClick={async () => {
                setReapplyError("");
                try {
                  // キャンセルする
                  await axiosClient.post(
                    `/api/requests/${currentSubmission.id}/reapply`,
                    { action: "cancel" }
                  );
                  setIsCancelled(true);
                  setIsEditable(false);
                  setSuccessMessage(
                    "Your request has been cancelled. Thank you for letting us know."
                  );
                  await loadRequestData();
                } catch (err: any) {
                  setReapplyError(
                    err.response?.data?.message || "Failed to process request."
                  );
                }
              }}
            >
              Cancel Request
            </button>
          </div>
          {reapplyError && (
            <div className="error-message" style={{ marginTop: 12 }}>
              {reapplyError}
            </div>
          )}
        </div>
      );
    }

    // reapplyStatus=respondedの場合：既に返信済み
    if (
      currentSubmission.status === "rejected" &&
      currentSubmission.reapplyStatus === "responded"
    ) {
      return (
        <div className="info-message">
          <p>
            Thank you for your reply! We are currently reviewing your request.
            We shall contact you via your registered contact details once a
            decision has been made.
          </p>
        </div>
      );
    }

    if (currentSubmission.status === "rejected") {
      return (
        <div className="rejection-note">
          <p>This request is on the waiting list.</p>
          <p>
            We have reached our maximum number of commissions and cannot start
            immediately. You will receive a notification at your registered
            email address when we can accept your request.
          </p>
        </div>
      );
    }

    if (!isEditable) {
      return (
        <div className="info-message">
          This commission has been accepted and can no longer be edited. If you
          need to make changes, please contact us using your registered contact
          information.
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (
    !loading &&
    commissionClosed &&
    (!data || data.submissions.length === 0) &&
    !data?.registration.vip
  ) {
    return (
      <div className="request-form-container">
        <div className="error-container">
          <h1>🚫 Submissions Closed</h1>
          <p>
            Commission submissions are currently closed.
            <br />
            We will notify you at your registered email address when submissions
            reopen.
          </p>
          <button onClick={() => navigate("/")} className="btn-back">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="request-form-container">
        <div className="error-container">
          <h1>❌ Error</h1>
          <p>{error}</p>
          <button onClick={() => navigate("/")} className="btn-back">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // VIPユーザーで複数依頼がある場合は一覧を表示
  if (data.registration.vip && !editingSubmissionId) {
    const activeSubmissions = data.submissions.filter(
      (sub) => sub.status !== "completed"
    );

    return (
      <div className="request-list-container">
        <h1>Your Commission Requests (VIP)</h1>
        <div className="submissions-list">
          {activeSubmissions && activeSubmissions.length > 0 ? (
            activeSubmissions.map((sub) => (
              <div key={sub.id} className="submission-card">
                <h3>{sub.name || "Untitled"}</h3>
                <p>
                  Status:{" "}
                  <span className="status">{statusLabel(sub.status)}</span>
                </p>
                <button onClick={() => setEditingSubmissionId(sub.id)}>
                  View Details
                </button>
              </div>
            ))
          ) : (
            <p className="no-submissions">No commission requests yet.</p>
          )}
        </div>
        <button
          className="btn-new"
          onClick={() => setEditingSubmissionId("new")}
        >
          + New Commission Request
        </button>
      </div>
    );
  }

  return (
    <div className="request-form-container">
      <div className="request-form-content">
        <div className="form-header">
          <h1>Illustration Commission Request Form</h1>
          {data.registration.vip && editingSubmissionId && (
            <button className="btn-back" onClick={handleBackToList}>
              ← Back to Request List
            </button>
          )}
        </div>

        {data.registration.vip && <div className="vip-badge">VIP User</div>}

        {data.registration.fastlane && (
          <div className="fastlane-badge">Priority User</div>
        )}

        {currentSubmission && (
          <div className="status-banner">
            <span className="label">Current Status:</span>
            <span className="status-pill">
              {statusLabel(currentSubmission.status)}
            </span>
          </div>
        )}

        {renderStatusNotice()}

        <form onSubmit={handleSubmit} className="request-form">
          <div className="form-group">
            <label htmlFor="name">
              Name <span style={{ color: "#f51d05" }}>*</span>
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              disabled={!isEditable || submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={data.registration.email}
              disabled
            />
            <small>
              This is your registered email address (cannot be changed).
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="artType">
              Type of Illustration <span style={{ color: "#f51d05" }}>*</span>
            </label>
            <select
              id="artType"
              value={formData.artType}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  artType: e.target.value as any,
                })
              }
              required
              disabled={!isEditable || submitting}
            >
              <option value="">Please select</option>
              <option value="standing">Character Only</option>
              <option value="single">Illustration With Background</option>
              <option value="single-layered">
                Illustration With Background + Layer
              </option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="deadline">
              Preferred Deadline (optional)
              <br />
              <span style={{ fontWeight: 400, fontSize: "13px" }}>
                (Production usually takes about one month. Deadlines within one
                month cannot be accepted.)
              </span>
            </label>
            <input
              id="deadline"
              type="date"
              value={formData.deadline}
              onChange={(e) =>
                setFormData({ ...formData, deadline: e.target.value })
              }
              disabled={!isEditable || submitting}
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.publicPermission}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    publicPermission: e.target.checked,
                  })
                }
                disabled={!isEditable || submitting}
              />
              Allow public display of completed work.
            </label>
            <label>
              <span style={{ fontWeight: 400, fontSize: "13px" }}>
                (If there are any specified release dates, please indicate them
                in the details section.)
              </span>
            </label>
          </div>

          <div className="form-group">
            <label>
              Preferred Contact Method{" "}
              <span style={{ color: "#f51d05" }}>*</span>
            </label>
            <div className="contact-group">
              <select
                value={formData.contactType}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contactType: e.target.value as any,
                  })
                }
                disabled={!isEditable || submitting}
              >
                <option value="email">Email</option>
                <option value="discord">Discord</option>
                <option value="x">X (Twitter)</option>
              </select>
              <input
                type="text"
                value={formData.contactValue}
                onChange={(e) =>
                  setFormData({ ...formData, contactValue: e.target.value })
                }
                placeholder={
                  formData.contactType === "email"
                    ? "your-email@example.com"
                    : formData.contactType === "discord"
                      ? "username#1234"
                      : "@username"
                }
                required
                disabled={!isEditable || submitting}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="detail">Commission Details</label>
            <textarea
              id="detail"
              value={formData.detail}
              onChange={(e) =>
                setFormData({ ...formData, detail: e.target.value })
              }
              rows={6}
              placeholder="Please describe the character, pose, background, or any other requests. You can also enter URLs here. To send images, please use the 'Image Upload' section below (up to 5 images). For more than 5 images, please use a storage service such as Google Drive."
              disabled={!isEditable || submitting}
            />
          </div>

          <div className="form-group">
            <label>Reference Links (optional)</label>
            {formData.links.map((link, index) => (
              <div key={index} className="link-input-group">
                <input
                  type="url"
                  value={link}
                  onChange={(e) => handleLinkChange(index, e.target.value)}
                  placeholder="https://..."
                  disabled={!isEditable || submitting}
                />
                {formData.links.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveLink(index)}
                    className="btn-remove"
                    disabled={!isEditable || submitting}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddLink}
              className="btn-add-link"
              disabled={!isEditable || submitting}
            >
              + Add Link
            </button>
          </div>

          <div className="form-group">
            <label>Image Upload (up to 5 images)</label>
            <div className="upload-section">
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={(e) => {
                  const files = e.currentTarget.files;
                  if (!files || files.length === 0) {
                    return;
                  }

                  uploadImages(files)
                    .then((results) => {
                      if (results) {
                        setFormData((prev) => ({
                          ...prev,
                          attachments: [
                            ...prev.attachments,
                            ...results.map((r) => ({
                              url: r.url,
                              previewUrl: r.previewUrl,
                            })),
                          ],
                        }));
                      }
                    })
                    .finally(() => {});
                }}
                disabled={!isEditable || uploading}
                style={{ display: "block", marginBottom: "12px" }}
              />
              {uploading && progress && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progress.percentage}%` }}
                    />
                  </div>
                  <p className="progress-text">
                    Uploading: {progress.percentage}%
                  </p>
                </div>
              )}
              {uploadError && (
                <div className="error-message">{uploadError}</div>
              )}
            </div>
            {formData.attachments.length > 0 && (
              <div className="uploaded-images">
                {formData.attachments.map((att, i) => (
                  <div key={i} className="image-preview">
                    <img src={att.previewUrl} alt={`upload-${i}`} />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          attachments: prev.attachments.filter(
                            (_, idx) => idx !== i
                          ),
                        }));
                      }}
                      className="btn-remove-image"
                      disabled={!isEditable}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
          {successMessage && (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h3>{successMessage}</h3>
              <div className="success-details">
                {isCancelled ? (
                  <>
                    <p>
                      Your request has been cancelled and will not be processed.
                    </p>
                    <p>
                      If you wish to submit a new commission, please re-register
                      your email address from the home page.
                    </p>
                    <button onClick={() => navigate("/")} className="btn-back">
                      Back to Home
                    </button>
                  </>
                ) : (
                  <>
                    <p>
                      You can make changes or resubmit your request at any time
                      using this page.
                    </p>
                    <p>
                      When your request is accepted, we will contact you via
                      your preferred contact method (Email, Discord, or X).
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {isEditable && (
            <>
              <button
                type="submit"
                className="btn-submit"
                disabled={submitting}
              >
                {submitting
                  ? "Submitting..."
                  : editingSubmissionId && editingSubmissionId !== "new"
                    ? "Update Commission Request"
                    : "Submit Commission Request"}
              </button>
              {/* Show cancel button only for existing, unapproved requests */}
              {editingSubmissionId &&
                editingSubmissionId !== "new" &&
                currentSubmission && (
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={handleCancelRequest}
                    disabled={submitting}
                  >
                    Cancel Commission Request
                  </button>
                )}
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default RequestForm;
